/**
 * Finding entries mentioned in plain text, ported from the desktop's `mentions.NameIndex`
 * and `mentions.MentionScanner` (docs/ENTRY-EDITOR-SLICE-2-PLAN.md section 4.2). Both
 * editions run `schema/fixtures/mention-cases.txt`, so the rules stay the same in each.
 *
 * Every entry answers to its title, its aliases, and short names worked out from the
 * title: leading ranks and honorifics removed ("Guard Captain Tomas" is "Tomas"), a
 * leading "The" dropped, and for a person their given name ("Arion Valen" is "Arion").
 * Titles and aliases match however they're capitalised; short names only with a capital.
 *
 * Text is compared in UTF-16 code units, as Java's `char`s are.
 */

/** How a name was recognised, strongest first. */
export type MatchKind = 'TITLE' | 'ALIAS' | 'SHORT_NAME';
const KIND_ORDER: Record<MatchKind, number> = { TITLE: 0, ALIAS: 1, SHORT_NAME: 2 };

/** What the index needs of an entry; `EntrySummary` has all of it. */
export interface MentionableEntry {
  readonly id: string;
  readonly title: string;
  readonly entityType: string;
  readonly aliases: readonly string[];
}

/** An entry a name in the text could refer to, and how that name matched it. */
export interface Candidate {
  readonly entryId: string;
  readonly title: string;
  readonly entityType: string;
  readonly kind: MatchKind;
}

/** A name found in text: `start` inclusive, `end` exclusive, and every entry it could mean. */
export interface Mention {
  readonly start: number;
  readonly end: number;
  readonly text: string;
  readonly candidates: readonly Candidate[];
}

/** Leading words that are ranks, honorifics or offices rather than part of the name. */
const HONORIFICS = new Set([
  'the', 'lord', 'lady', 'sir', 'dame', 'king', 'queen', 'prince', 'princess', 'duke', 'duchess',
  'count', 'countess', 'baron', 'baroness', 'earl', 'emperor', 'empress', 'warlord', 'general', 'captain',
  'commander', 'lieutenant', 'sergeant', 'corporal', 'seneschal', 'castellan', 'steward', 'chancellor',
  'marshal', 'admiral', 'knight', 'master', 'mistress', 'brother', 'sister', 'father', 'mother', 'saint',
  'st', 'st.', 'archmage', 'magister', 'elder', 'chief', 'chieftain', 'doctor', 'dr', 'dr.', 'professor',
  'mr', 'mr.', 'mrs', 'mrs.', 'ms', 'ms.', 'madam', 'guard', 'watch', 'high', 'grand', 'priest',
  'priestess', 'abbot', 'abbess', 'bishop', 'cardinal', 'archbishop', 'regent', 'magistrate', 'reeve',
  'lady-in-waiting', 'ser',
]);

/** Short names that are also everyday words, which would match far too often. */
const COMMON_WORDS = new Set([
  'the', 'and', 'war', 'king', 'queen', 'house', 'city', 'old', 'new', 'red', 'black', 'white', 'high',
  'great', 'little', 'one', 'two', 'first', 'last', 'north', 'south', 'east', 'west', 'iron', 'stone',
  'silver', 'gold', 'will', 'may', 'hope', 'grace',
]);

/** Java's `\s` in `split("\\s+")`: ASCII whitespace only. */
const JAVA_SPACES = /[ \t\n\x0B\f\r]+/;
const LETTER_OR_DIGIT = /^[\p{L}\p{Nd}]$/u;
const UPPER_CASE = /^\p{Uppercase}$/u;

/** `Character.isLetterOrDigit(char)`: a lone surrogate is neither. */
export function isLetterOrDigit(c: string): boolean {
  return LETTER_OR_DIGIT.test(c);
}

function isWordChar(c: string): boolean {
  return c === '_' || isLetterOrDigit(c);
}

/** `Character.isUpperCase(char)`: Lu, plus the other upper-case letters such as Ⓐ. */
export function isUpperCase(c: string): boolean {
  return UPPER_CASE.test(c);
}

/** `Character.toUpperCase(char)`, a single-character mapping. */
function upper(c: string): string {
  const u = c.toUpperCase();
  return u.length === 1 ? u : c;
}

function lower(c: string): string {
  const l = c.toLowerCase();
  return l.length === 1 ? l : c;
}

/** `text.regionMatches(true, start, name, 0, name.length())`. */
function regionMatchesIgnoreCase(text: string, start: number, name: string): boolean {
  if (start + name.length > text.length) return false;
  for (let k = 0; k < name.length; k++) {
    const a = text[start + k]!;
    const b = name[k]!;
    if (a === b) continue;
    const ua = upper(a);
    const ub = upper(b);
    if (ua === ub || lower(ua) === lower(ub)) continue;
    return false;
  }
  return true;
}

function byKind(a: Candidate, b: Candidate): number {
  return KIND_ORDER[a.kind] - KIND_ORDER[b.kind];
}

export class NameIndex {
  /** Lower-cased name to every entry it names, strongest kind first. */
  private readonly byName = new Map<string, Candidate[]>();
  /** Names grouped by their lower-cased first character, longest first, for scanning. */
  private readonly byFirstChar = new Map<string, string[]>();
  private readonly entries = new Map<string, MentionableEntry>();

  private constructor() {}

  static of(entries: Iterable<MentionableEntry>): NameIndex {
    const index = new NameIndex();
    for (const entry of entries) {
      index.entries.set(entry.id, entry);
      for (const name of namesOf(entry)) index.add(name.text, name.candidate);
    }
    for (const list of index.byFirstChar.values()) list.sort((a, b) => b.length - a.length);
    for (const list of index.byName.values()) list.sort(byKind);
    return index;
  }

  private add(text: string, candidate: Candidate): void {
    const key = text.toLowerCase();
    let list = this.byName.get(key);
    if (!list) {
      list = [];
      this.byName.set(key, list);
      const first = key[0]!;
      this.byFirstChar.set(first, [...(this.byFirstChar.get(first) ?? []), key]);
    }
    // One entry, one candidate per name: its strongest way of being called that.
    const weaker = list.findIndex((c) => c.entryId === candidate.entryId && byKind(c, candidate) > 0);
    if (weaker >= 0) list.splice(weaker, 1);
    if (!list.some((c) => c.entryId === candidate.entryId)) list.push(candidate);
  }

  /** The title, aliases and short names `entry` can be mentioned by. */
  static namesFor(entry: MentionableEntry): string[] {
    return [...new Set(namesOf(entry).map((name) => name.text))];
  }

  /** Names that start with `c` (lower-cased), longest first. */
  namesStartingWith(c: string): readonly string[] {
    return this.byFirstChar.get(lower(c)) ?? [];
  }

  /** Candidates for a lower-cased name, strongest kind first. */
  candidates(lowercasedName: string): readonly Candidate[] {
    return this.byName.get(lowercasedName) ?? [];
  }

  /** Whether any entry answers to `name`, however capitalised. */
  knows(name: string): boolean {
    return this.byName.has(name.toLowerCase());
  }

  entry(id: string): MentionableEntry | undefined {
    return this.entries.get(id);
  }
}

function namesOf(entry: MentionableEntry): { text: string; candidate: Candidate }[] {
  const candidate = (kind: MatchKind): Candidate => ({ entryId: entry.id, title: entry.title, entityType: entry.entityType, kind });
  const title = entry.title.trim();
  const names = [{ text: title, candidate: candidate('TITLE') }];
  // As `LoreEntry` keeps them: trimmed, not blank, not the title, no repeats.
  const aliases = [...new Set(entry.aliases.map((a) => a.trim()).filter((a) => a !== '' && a.toLowerCase() !== title.toLowerCase()))];
  for (const alias of aliases) names.push({ text: alias, candidate: candidate('ALIAS') });

  const shorts = new Set<string>();
  const words = title.split(JAVA_SPACES);
  while (words.length > 1 && HONORIFICS.has(words[0]!.toLowerCase())) words.shift();
  const stripped = words.join(' ');
  if (stripped.toLowerCase() !== title.toLowerCase()) shorts.add(stripped);
  if (entry.entityType === 'PERSON' && words.length >= 2) {
    // "Arion Valen" -> "Arion"; "Tamsin of Red Hollow" -> "Tamsin".
    shorts.add(words[0]!);
  }
  for (const name of shorts) {
    const bare = name.replace(/[,.;:]+$/, '');
    if (bare.length >= 3 && !COMMON_WORDS.has(bare.toLowerCase()) && isUpperCase(bare[0]!)) {
      names.push({ text: bare, candidate: candidate('SHORT_NAME') });
    }
  }
  return names;
}

/**
 * The entries `text` mentions: at each word start, the longest known name on word
 * boundaries, without overlaps, so "Guard Captain Tomas" is one mention, not three. A
 * possessive still counts ("Kessian's"). When a name means several entries, only those
 * matched by the strongest kind are kept.
 */
export function scanMentions(text: string, index: NameIndex): Mention[] {
  const mentions: Mention[] = [];
  let i = 0;
  const n = text.length;
  while (i < n) {
    const c = text[i]!;
    if (!isLetterOrDigit(c) || (i > 0 && isWordChar(text[i - 1]!))) {
      i++;
      continue;
    }
    let found: Mention | null = null;
    for (const name of index.namesStartingWith(c)) {
      const end = i + name.length;
      if (end > n || !regionMatchesIgnoreCase(text, i, name) || (end < n && isWordChar(text[end]!))) continue;
      const candidates = index.candidates(name);
      const strongest = candidates[0]!.kind;
      if (strongest === 'SHORT_NAME' && !isUpperCase(c)) continue;
      found = { start: i, end, text: text.slice(i, end), candidates: candidates.filter((candidate) => candidate.kind === strongest) };
      break;
    }
    if (found) {
      mentions.push(found);
      i = found.end;
    } else {
      i++;
    }
  }
  return mentions;
}
